import { createContext, ReactNode, useState, useContext } from 'react';

import {
  IAutoEnrollmentApplication,
  getEnrollmentApplicationHelpers,
} from '@sureapp/platform-sdk';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import api from '@@/api';
import { useConfig } from '@@/auto/services/useConfig';
import { clearQuote, getAutoQuote } from '@@/auto/store/actions';
import { useTimeout } from '@@/common/hooks/useTimeout';

interface ICreateQuoteOptions {
  quoteRedirectPath?: string;
}

interface IQAStartScreensContext {
  createQuote: (
    enrollment: IAutoEnrollmentApplication,
    options?: ICreateQuoteOptions
  ) => Promise<void>;
  hasCreatedQuote: boolean;
  isSubmittingQuickQuote: boolean;
}

const QAStartScreensContext = createContext<IQAStartScreensContext | undefined>(
  undefined
);

type TQAStartScreensContextProvider = {
  children?: ReactNode;
};

export function QAStartScreensContextProvider({
  children,
}: TQAStartScreensContextProvider) {
  const {
    config: { partnerCode },
  } = useConfig();
  const dispatch = useDispatch();
  const history = useHistory();
  const { setTimeoutSafe } = useTimeout();
  const [isSubmittingQuickQuote, setIsSubmittingQuickQuote] = useState(false);
  const [hasCreatedQuote, setHasCreatedQuote] = useState(false);

  async function getQuoteUntilAdpfResolved(
    quoteNumber: string,
    quoteRedirectPath: string
  ) {
    const autoQuote = await getAutoQuote(quoteNumber)(dispatch);
    const adpf = autoQuote.application.metadata.third_party_data_orders[0];

    switch (adpf.status) {
      case 'ready':
      case 'pending':
        setTimeoutSafe(
          () => getQuoteUntilAdpfResolved(quoteNumber, quoteRedirectPath),
          4000
        );
        break;
      case 'success':
        clearQuote()(dispatch);
        history.push(
          `/auto/quotes/${autoQuote.quote_number}${quoteRedirectPath}`
        );
        setHasCreatedQuote(true);
        setIsSubmittingQuickQuote(false);
        break;
      default:
        setIsSubmittingQuickQuote(false);
        break;
    }
  }

  async function createQuote(
    enrollmentApplication: IAutoEnrollmentApplication,
    options: ICreateQuoteOptions = {
      quoteRedirectPath: '/health-check/verify',
    }
  ) {
    setIsSubmittingQuickQuote(true);
    setHasCreatedQuote(false);
    const {
      addReadyAdpfOrder,
      getQuoteFromEnrollmentApplication,
    } = getEnrollmentApplicationHelpers({
      enrollmentApplication,
      verticalCode: 'toggle_auto',
      partnerCode,
    });
    try {
      const newQuote = await api.createQuote(
        addReadyAdpfOrder(getQuoteFromEnrollmentApplication())
      );
      await getQuoteUntilAdpfResolved(
        newQuote.quote_number,
        options.quoteRedirectPath!
      );
    } catch (error) {
      setIsSubmittingQuickQuote(false);
      throw error;
    }
  }

  return (
    <QAStartScreensContext.Provider
      value={{ createQuote, hasCreatedQuote, isSubmittingQuickQuote }}
    >
      {children}
    </QAStartScreensContext.Provider>
  );
}

export function useQAStartScreenContext() {
  const context = useContext(QAStartScreensContext);
  if (!context) {
    throw new Error(
      'useQAStartScreenContext must be used within a QAStartScreensContextProvider'
    );
  }

  return context;
}
